import { useAtom } from 'jotai/react';
import { Checkbox, FormControlLabel } from '@mui/material';
import { ChangeEvent, useState } from 'react';

import atoms from '../../../atoms';
import * as styles from '../styles.css';
import Input from '../../common/input/base-input';
import { getYearRange } from './helpers';
import SingleSelect from '../../common/single-select/base-single-select';
import { IEducation } from '../../../interfaces';

const degrees = [
  "Associate's",
  "Bachelor's (in progress)",
  "Bachelor's",
  "Master's/Graduate",
  'Doctorate (e.g., PhD, MD, JD, etc.)',
];

export interface IInputBoxProps {
  id: string;
  value: IEducation;
  isFirst?: boolean;
  setValue: (value) => void;
  onRemove?: () => void;
}

export const InputBox = ({ id, value, setValue }: IInputBoxProps) => {
  const [institutions, filterInstitutions] = useAtom<string[], [string], void>(
    atoms.institutions.search,
  );

  const [noGPA, setNoGPA] = useState(value.gpa === null);

  const customTransformationGPA = (value: string) => {
    if (parseFloat(value) < 0) {
      return '0';
    }
    if (parseFloat(value) > 4) {
      return '4';
    }
    return value;
  };

  const handleNoGPAChange = (e: ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setNoGPA(checked);
    setValue({ gpa: checked ? null : '' });
  };

  return (
    <div id={id} className={styles.inputBox}>
      <div className={styles.input}>
        <SingleSelect
          id={`${id}-school`}
          freeSolo
          label="University/College (highest level)"
          value={value.school}
          onSelect={(school) => setValue({ school })}
          options={institutions}
          placeholder="Search for your school"
          onChange={async (value) => void filterInstitutions(value)}
        />
      </div>
      <div className={styles.input}>
        <SingleSelect
          id={`${id}-degree`}
          label="Degree"
          value={value.degree}
          options={degrees}
          placeholder="Select Degree type"
          onSelect={(degree) => setValue({ degree })}
        />
      </div>
      <div className={styles.input}>
        <Input
          id={`${id}-major`}
          label="Major"
          value={value.major}
          placeholder="Enter your major"
          onChange={(major) => setValue({ major })}
        />
      </div>
      <div className={styles.input}>
        <SingleSelect
          id={`${id}-graduation_year`}
          label="Graduation Year (or expected)"
          value={value.graduation_year}
          placeholder="Select Year"
          options={getYearRange()}
          onSelect={(graduation_year) => setValue({ graduation_year })}
        />
      </div>
      <div className={styles.input}>
        <Input
          id={`${id}-gpa`}
          type="number"
          label="GPA (approximate)"
          value={noGPA ? undefined : value.gpa?.toString() || ''}
          placeholder="Enter your GPA"
          onChange={(gpa: string) => {
            setValue({ gpa: gpa === '' ? null : parseFloat(gpa) });
          }}
          customTransformation={customTransformationGPA}
          disabled={noGPA}
          step={0.1}
          min={0}
          max={4}
          clearValue={noGPA}
        />
        <FormControlLabel
          className={styles.checkbox}
          control={<Checkbox id={`${id}-no-gpa`} checked={noGPA} onChange={handleNoGPAChange} />}
          label="My college/university did not use a traditional GPA system."
        />
      </div>
    </div>
  );
};
